import Checkbox from '@shared/components/Checkbox/Checkbox';
import ErrorMessage from '@shared/components/ErrorMessage/ErrorMessage';
import Input from '@shared/components/InputGroup/Input';
import Select from '@shared/components/Select/Select';
import { Controller } from 'react-hook-form';

import useCustomReminder from './useCustomReminder';
import { getMaxAmountInputValue } from './utils';
import useCreateNewRoomDataContext from '../../CreateNewRoomDataProvider/useCreateNewRoomDataContext';
import { getUnitSelectOptions, getUnitTranslation } from '../utils';

interface CustomReminderProps {
  index: number;
}

const CustomReminder = ({ index }: CustomReminderProps) => {
  const {
    roomForm: {
      watch,
      control,
      formState: { errors },
    },
  } = useCreateNewRoomDataContext();
  const {
    reminderEnabledKey,
    reminderAmountKey,
    reminderUnitKey,
    handleInputChange,
    handleDropDownChange,
    handleInputKeyDown,
    handleCheckboxChange,
    handlePaste,
  } = useCustomReminder(index);

  const remindersWatch = watch('reminders');
  const currentReminderAmountWatch = watch(reminderAmountKey) || 0;
  const currentReminderUnitWatch = watch(reminderUnitKey);
  const isEnabledRemindersLimitReached = remindersWatch.filter(({ enabled }) => enabled).length >= 3;
  const reminderError = errors.reminders?.[index];
  const errorMessage = reminderError?.amount?.message;
  const maxAmountInputValue = getMaxAmountInputValue(currentReminderUnitWatch);

  return (
    <div className="flex w-full flex-col items-start gap-2">
      <div className="w-full flex items-center gap-4">
        <Controller
          name={reminderEnabledKey}
          control={control}
          render={({ field: { onChange, value } }) => {
            const isDisabled = !currentReminderAmountWatch || (!value && isEnabledRemindersLimitReached);

            return (
              <Checkbox
                checked={value}
                containerClassName="!space-x-0"
                onChange={handleCheckboxChange(onChange)}
                disabled={isDisabled}
              />
            );
          }}
        />
        <div className="w-full flex gap-2">
          <Controller
            name={reminderAmountKey}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                variant="white-bg"
                type="number"
                onChange={handleInputChange(onChange)}
                value={value || ''}
                containerClassName="!w-[62px] sm:!w-[142px]"
                onKeyDown={handleInputKeyDown}
                onPaste={handlePaste}
                min={0}
                max={maxAmountInputValue}
              />
            )}
          />
          <Controller
            name={reminderUnitKey}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                wrapperClassName="grow"
                className="!bg-gray-10"
                options={getUnitSelectOptions(currentReminderAmountWatch, handleDropDownChange(onChange))}
              >
                {getUnitTranslation(value, currentReminderAmountWatch)}
              </Select>
            )}
          />
        </div>
      </div>
      <ErrorMessage message={errorMessage} className="!mt-0" />
    </div>
  );
};

export default CustomReminder;
